/* Drawings from the drawing kit (snippets/drawing.liquid): callouts over the finished drawing, and the kit's assembly
   on the few drawings marked data-assemble. Ported from the Shop Drawing prototype's main.js.

   The finished drawing (img.full) is in the HTML and is the resting state: it shows at once and stays unless this
   script has something fully ready to put over it. Each figure's manifest (data-manifest, assets/kit-<scene>.json,
   written by tools/kit-to-theme.mjs) gives the drawing's size, its layers, the anchors, and the callouts with their
   text. Two things go over the finished drawing, in .art (the manifest's aspect, contained in the drawing's box as the
   img is):
   - the dimension lines, leaders, angle arcs, and timber break symbols, as SVG in CSS px, when it comes into view;
   - the layer stack of the kit's assembly animation (the kit's README, "The assembly animation"), only on figures
     marked data-assemble, only once the visitor has left the top of the page, only for a drawing still wholly out
     of view once every layer image has loaded, and played when it comes into view.
   Nothing here reads the scroll position: IntersectionObservers say when a drawing is near and when it is in view.
   Animation is transform and opacity only, inside boxes CSS has sized, so nothing moves the layout. Reduced motion,
   no IntersectionObserver, no manifest, or no JS: the finished drawing, as it is. */
(() => {
  const figs = [...document.querySelectorAll('.dwg[data-manifest], .dwg[data-dxf]')];
  if (!figs.length) return;
  const root = document.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const canAnimate = () => root.classList.contains('anim') && !reduce.matches;
  const SVGNS = 'http://www.w3.org/2000/svg';
  const EASE = 'cubic-bezier(.22, .7, .28, 1)';
  const BUDGET = 1600;   // an assembly not finished this long after it started ends at the finished drawing
  // A manifest may give its figure a longer budget (`budget`, ms: the knife plate post base's, about 3 s, the owner's
  // OK of 16 October 2026); its phases are then paced to end, slide and all, by nine tenths of it. Every other figure
  // keeps BUDGET and its timing.
  // A slide ending (`shift`): after the last phase lands, a short HOLD, then the assembly slides home in SLIDE ms.
  const HOLD = 150, SLIDE = 360;

  /* ---------- loading ---------- */
  // A failed manifest isn't remembered: the drawing's next time in view fetches it again, three tries in all.
  const manifests = new Map(), tries = new Map();
  function manifest(url) {
    if (!manifests.has(url)) {
      const n = (tries.get(url) || 0) + 1; tries.set(url, n);
      if (n > 3) return Promise.resolve(null);
      const p = fetch(url).then(r => (r.ok ? r.json() : null)).then(m => (m && m.width && m.height ? m : null)).catch(() => null);
      p.then(m => { if (!m && manifests.get(url) === p) manifests.delete(url); });
      manifests.set(url, p);
    }
    return manifests.get(url);
  }
  const within = (p, ms) => Promise.race([p, new Promise((_, no) => setTimeout(() => no(new Error('slow')), ms))]);
  const imgLoaded = img => (img.complete ? Promise.resolve(img.naturalWidth > 0)
    : new Promise(r => { img.addEventListener('load', () => r(true), { once: true }); img.addEventListener('error', () => r(false), { once: true }); }));
  const inViewport = el => { const r = el.getBoundingClientRect(); return r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth; };

  // At most three layer images download at once, so they never hold every connection while a finished drawing the
  // visitor has reached is waiting for one. slot(img, url) resolves true (loaded) or false (failed or dropped).
  const SLOTS = 3, waiting = [], jobs = new WeakMap(); let busy = 0;
  function slot(img, url) {
    return new Promise(resolve => {
      const job = { started: false, ended: false };
      job.end = ok => {
        if (job.ended) return; job.ended = true;
        if (job.started) { busy--; const next = waiting.shift(); if (next) next.go(); }
        resolve(ok);
      };
      job.go = () => {
        job.started = true; busy++;
        img.addEventListener('load', () => job.end(true), { once: true });
        img.addEventListener('error', () => job.end(false), { once: true });
        img.src = url;
      };
      jobs.set(img, job);
      if (busy < SLOTS) job.go(); else waiting.push(job);
    });
  }
  function drop(img) {
    const job = jobs.get(img); if (!job || job.ended) return;
    const k = waiting.indexOf(job); if (k >= 0) waiting.splice(k, 1);
    if (job.started) img.removeAttribute('src');
    job.end(false);
  }

  /* ---------- the assembly ---------- */
  // Builds the layer wrappers and starts their images loading at low priority. `loaded` resolves when all are in;
  // `decode(ph)` decodes one phase's images; `abort()` stops what is still loading.
  // A slide ending (the manifest's `shift`, and a layer with reveal 'slide': the kit's bare copy of a part, drawn left of
  // the assembly): the copy stays at the bottom of the stack, hidden, and every other layer goes in one group held
  // left by the shift, so the assembly is played where the copy stands; playStack then shows the copy and slides the
  // group home. The finished drawing has both side by side, so reduced motion and no JS need nothing of this.
  function buildStack(m, base) {
    const stack = document.createElement('div'); stack.className = 'stack';
    const sh = m.shift, slides = !!(sh && (sh.dx || sh.dy) && m.layers.some(l => l.reveal === 'slide'));
    const group = slides ? document.createElement('div') : stack;
    let bare = null, held = '';
    if (slides) {
      group.className = 'grp';
      held = `translate(${(-100 * sh.dx) / m.width}%, ${(-100 * (sh.dy || 0)) / m.height}%)`;
      group.style.transform = held;
    }
    const imgs = new Map(), byPhase = new Map(), all = [], loads = [];
    // a clip is also fetched by CSS as a mask, a CORS request: loading it the same way lets the mask reuse it
    const load = (src, ph, cors) => {
      const i = new Image(); i.alt = ''; if (cors) i.crossOrigin = 'anonymous'; i.decoding = 'async'; i.fetchPriority = 'low';
      loads.push(slot(i, new URL(src, base).href));
      if (!imgs.has(ph)) imgs.set(ph, []);
      imgs.get(ph).push(i); all.push(i); return i;
    };
    for (const l of m.layers) {
      const ph = l.phase || 0, w = document.createElement('div'); w.className = 'ly';
      if (!ph) {
        const src = l.stage || l.file; if (!src) continue;
        w.append(load(src, 0));
        if (slides && l.reveal === 'slide') { w.style.visibility = 'hidden'; bare = w; stack.append(w); }
        else group.append(w);
        continue;
      }
      const fly = l.solo || l.stage; if (!fly) continue;
      const L = { l, w, solo: load(fly, ph) };
      w.append(L.solo);
      if (l.stage) { L.stage = load(l.stage, ph); L.stage.style.opacity = '0'; w.append(L.stage); }
      if (l.clip) { load(l.clip, ph, true); w.style.webkitMaskImage = w.style.maskImage = `url("${new URL(l.clip, base).href}")`; }
      w.style.visibility = 'hidden';
      group.append(w);
      if (!byPhase.has(ph)) byPhase.set(ph, []);
      byPhase.get(ph).push(L);
    }
    if (!byPhase.size) return null;
    if (slides) stack.append(group);
    if (slides && !bare) group.style.transform = '';   // no copy to slide off: play it in place
    const slide = slides && bare ? { bare, group, held } : null;
    const decoded = new Map();
    const decode = ph => {
      if (!decoded.has(ph)) { const p = Promise.all((imgs.get(ph) || []).map(i => i.decode())); p.catch(() => {}); decoded.set(ph, p); }
      return decoded.get(ph);
    };
    const loaded = Promise.all(loads).then(oks => { if (!oks.every(Boolean)) throw new Error('missing layer'); });
    loaded.catch(() => {});
    return { stack, byPhase, phases: [...byPhase.keys()].sort((a, b) => a - b), loaded, decode, abort: () => all.forEach(drop), slide };
  }

  // Plays the phases in order: each layer slides home from its explode vector on its solo image, under its fixed clip
  // mask, then cross-fades to its stage image (110 ms). A phase is 900 ms / phases (160 to 340 ms); with the manifest's
  // own budget, the phases share nine tenths of it, less the last cross-fade and any slide (up to 900 ms each). `onLast`
  // runs as the last phase starts, so the callouts fade in while it lands. A phase not decoded in time ends it early.
  // With a slide ending, once every phase has landed the bare copy shows under the held group (the kit checks it is the
  // group's own bare part's image, moved), the group slides home, and only then do the callouts come, since they belong
  // where the assembly ends.
  async function playStack(art, m, plan, live, onLast) {
    const n = plan.phases.length, tail = 110 + (plan.slide ? HOLD + SLIDE : 0);
    const s = art.clientWidth / m.width;
    const per = m.budget ? Math.max(160, Math.min(900, (0.9 * m.budget - tail) / n)) : Math.max(160, Math.min(340, 900 / n));
    let fades = [];
    for (let k = 0; k < plan.phases.length; k++) {
      const ph = plan.phases[k];
      if (k + 1 < plan.phases.length) plan.decode(plan.phases[k + 1]);
      await within(plan.decode(ph), 300);
      if (!live()) return;
      if (k === plan.phases.length - 1 && !plan.slide) onLast();
      const list = plan.byPhase.get(ph);
      await Promise.all(list.map(L => {
        L.w.style.visibility = '';
        const e = L.l.explode, dx = e.dx * s, dy = e.dy * s;
        // no screen travel (in place, or along the line of sight): it fades in where it lands
        return (e.mode === 'screen' && (dx || dy)
          ? L.solo.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: per, easing: EASE })
          : L.solo.animate([{ opacity: 0 }, { opacity: 1 }], { duration: per * 0.9, easing: 'ease-out' })).finished;
      }));
      if (!live()) return;
      // landed: cross-fade to the stage image, or drop a layer hidden at rest; then release the solo image and clip
      fades = fades.concat(list.map(L => {
        if (!L.stage) { L.w.remove(); return null; }
        return L.stage.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 110, fill: 'forwards' }).finished.then(() => {
          L.stage.style.opacity = '1'; L.solo.remove(); L.w.style.webkitMaskImage = L.w.style.maskImage = '';
        });
      }));
    }
    await Promise.all(fades);
    const sl = plan.slide;
    if (!sl || !live()) return;
    await new Promise(r => setTimeout(r, HOLD));
    if (!live()) return;
    sl.bare.style.visibility = '';
    sl.group.style.transform = '';
    await sl.group.animate([{ transform: sl.held }, { transform: 'none' }], { duration: SLIDE, easing: EASE }).finished;
    if (live()) onLast();
  }

  /* ---------- callouts ---------- */
  const el = (name, attrs, parent) => {
    const e = document.createElementNS(SVGNS, name);
    for (const k in attrs) e.setAttribute(k, attrs[k]);
    if (parent) parent.append(e);
    return e;
  };
  const sub = (a, b) => [a[0] - b[0], a[1] - b[1]], add = (a, b) => [a[0] + b[0], a[1] + b[1]], mul = (a, k) => [a[0] * k, a[1] * k];
  const len = a => Math.hypot(a[0], a[1]), unit = a => { const l = len(a) || 1; return [a[0] / l, a[1] / l]; };
  const pts = (...p) => p.map(q => q.map(v => v.toFixed(1)).join(',')).join(' ');
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const textW = (t, cls) => t.length * (cls === 'lbl' ? 6.4 : 6.7) + 4;
  // boxes are [x0, y0, x1, y1]
  const box = (cx, cy, w, h) => [cx - w / 2, cy - h / 2, cx + w / 2, cy + h / 2];
  const overlaps = (a, b) => a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];
  // does segment p-q pass through box b (Liang-Barsky)?
  function crosses(p, q, b) {
    let t0 = 0, t1 = 1; const d = sub(q, p);
    for (const [pp, qq] of [[-d[0], p[0] - b[0]], [d[0], b[2] - p[0]], [-d[1], p[1] - b[1]], [d[1], b[3] - p[1]]]) {
      if (pp === 0) { if (qq < 0) return false; continue; }
      const r = qq / pp;
      if (pp < 0) { if (r > t1) return false; if (r > t0) t0 = r; } else { if (r < t0) return false; if (r < t1) t1 = r; }
    }
    return true;
  }

  // Timber cut ends: the kit marks each cut with its corners (break-<member>-<end>-1..4). Each cut gets one long-break
  // symbol on one edge of its cut face: an edge with both corners visible, on the member's outline (the convex hull of
  // all its cut corners), preferring an edge not shared with another cut, then the longest.
  function breaks(m) {
    const members = new Map();
    for (const a of m.anchors) {
      const k = /^break-(.+)-([^-]+)-([1-4])$/.exec(a.id); if (!k) continue;
      if (!members.has(k[1])) members.set(k[1], new Map());
      const ends = members.get(k[1]); if (!ends.has(k[2])) ends.set(k[2], []);
      ends.get(k[2])[k[3] - 1] = a;
    }
    const near = (p, q) => Math.hypot(p.x - q.x, p.y - q.y) < 1.5, out = [];
    for (const ends of members.values()) {
      const all = [...ends.values()].flat().filter(Boolean);
      const onHull = (p, q) => {
        let pos = 0, neg = 0; const tol = Math.hypot(q.x - p.x, q.y - p.y) * 1.5;
        for (const r of all) { const cr = (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x); if (cr > tol) pos++; else if (cr < -tol) neg++; }
        return !(pos && neg);
      };
      for (const [end, c] of ends) {
        const others = [...ends].filter(([e]) => e !== end).map(([, v]) => v.filter(Boolean));
        const shared = (p, q) => others.some(o => o.some(r => near(r, p)) && o.some(r => near(r, q)));
        let best = null, score = -1;
        for (let i = 0; i < 4; i++) {
          const p = c[i], q = c[(i + 1) % 4];
          if (!p || !q || p.hidden || q.hidden) continue;
          const L = Math.hypot(p.x - q.x, p.y - q.y); if (L < 2 || !onHull(p, q)) continue;
          const sc = L + (shared(p, q) ? 0 : 1e6);
          if (sc > score) { score = sc; best = [p, q]; }
        }
        if (best) out.push(best);
      }
    }
    return out;
  }

  function drawCallouts(fig, art, m, animate) {
    art.querySelector('svg.co')?.remove();
    const W = art.clientWidth, H = art.clientHeight, s = W / m.width;
    if (!W) return;
    // small drawings (under 240 px both ways) get only their first-level callouts
    const level = Math.min(+fig.dataset.callouts || 0, Math.max(W, H) < 240 ? 1 : 2);
    const rank = { dim: 0, angle: 0, leader: 1 };
    // dimensions first, so leaders can be placed clear of them
    const specs = (m.callouts || []).filter(c => c.lv <= level).map((c, i) => [c, i])
      .sort((a, b) => rank[a[0].t] - rank[b[0].t] || a[1] - b[1]).map(c => c[0]);
    const cuts = breaks(m);
    if (!specs.length && !cuts.length) return;
    // labels may use the figure's whole box, not just the drawing's
    const ar = art.getBoundingClientRect(), fr = fig.getBoundingClientRect();
    const X0 = fr.left - ar.left + 4, X1 = fr.right - ar.left - 4, Y0 = fr.top - ar.top + 4, Y1 = fr.bottom - ar.top - 4;
    const byId = new Map(m.anchors.map(a => [a.id, a]));
    const ok = (...ids) => ids.every(id => { const a = byId.get(id); return a && !a.hidden; });
    const P = id => { const a = byId.get(id); return [a.x * s, a.y * s]; };
    // A figure with data-plate-links makes each plate's callout a link to the plate's page: its label, leader and dot,
    // and a ring over the plate itself (a leader with a `plate`, 'King post plate (W4)' -> the W4's page; an export
    // from before the callouts were named began its label with the code, 'W4 peak'). The attribute is the plates'
    // pages by code (snippets/plate-urls.liquid, found by SKU); a code not in it gets no link
    let urls = null;
    if (fig.hasAttribute('data-plate-links')) { try { urls = JSON.parse(fig.dataset.plateLinks || '{}'); } catch { urls = {}; } }
    const links = !!urls;
    const svg = el('svg', links ? { class: 'co', width: W, height: H, viewBox: `0 0 ${W} ${H}` } : { class: 'co', width: W, height: H, viewBox: `0 0 ${W} ${H}`, 'aria-hidden': 'true' });
    // what a label keeps clear of: texts already placed, dimension and extension lines, and other leaders' dots
    const groups = [], boxes = [], lines = [];
    const dots = specs.filter(c => c.t === 'leader' && ok(c.at)).map(c => ({ c, b: box(...P(c.at), 8, 8) }));

    for (const [p1, p2] of cuts) {
      // long-break symbol: a line along the cut edge, a little past both corners, with one sharp Z at its middle;
      // under about 14 px it reads as a cross, not a break, and is left out
      const a = [p1.x * s, p1.y * s], b = [p2.x * s, p2.y * s], L = len(sub(b, a));
      if (L < 14) continue;
      const g = el('g', { class: 'brk' }, svg);
      const u = unit(sub(b, a)), n = [-u[1], u[0]], mid = mul(add(a, b), 0.5);
      const z = Math.min(L * 0.1, 5), hz = clamp(L * 0.16, 3, 6), e = clamp(L * 0.08, 2, 5);
      const d = 'M' + pts(sub(a, mul(u, e)), sub(mid, mul(u, z)), add(sub(mid, mul(u, z / 3)), mul(n, hz)),
        sub(add(mid, mul(u, z / 3)), mul(n, hz)), add(mid, mul(u, z)), add(b, mul(u, e))).replace(/ /g, 'L');
      el('path', { d, class: 'halo' }, g); el('path', { d }, g);
      groups.push(g);
    }

    for (const c of specs) {
      const g = el('g', {}, svg);
      if (c.t === 'dim') {
        if (!ok(c.a, c.b) || (c.fa && !ok(c.fa, c.fb)) || (c.offTo && !ok(c.offTo))) { g.remove(); continue; }
        let a = P(c.a), b = P(c.b), out;
        const d = unit(sub(b, a)); let n = [-d[1], d[0]];
        if (c.fa) {
          // the kit's own dimension line, with extension lines from the feature anchors
          const fa = P(c.fa), fb = P(c.fb); out = unit(sub(a, fa));
          const e1 = [add(fa, mul(out, 3)), add(a, mul(out, 5))], e2 = [add(fb, mul(out, 3)), add(b, mul(out, 5))];
          el('polyline', { class: 'ext', points: pts(...e1) }, g); el('polyline', { class: 'ext', points: pts(...e2) }, g);
          lines.push(e1, e2);
        } else {
          let off;
          if (c.offTo) {
            // `pad` below that anchor, opened up on a large drawing (up to 70%) and on a small one (up to 14 px, below
            // 0.8 of its size), so labels under the anchor's member clear the line: a plate's name with its code
            // under it is two lines, 30 px
            off = (P(c.offTo)[1] - a[1]) + (c.pad || 16) * (1 + 0.7 * clamp((s - 0.6) / 0.5, 0, 1)) + 14 * clamp((0.8 - s) / 0.2, 0, 1); n = [0, 1];
          } else {
            off = Math.max(12, (c.off || 16) * Math.min(1, s * 1.6));
            // on the top edge of a square member's end face in an isometric, the face's far edge is twice the edge's
            // rise above it: a plumb dimension clears the end grain past that
            if (c.clearFace) off = Math.max(off, 2 * Math.abs(b[1] - a[1]) + 8);
            // extension lines run square to the dimension, or along a model axis as drawn (`along`); else away from
            // the drawing's center
            if (c.along) n = unit(c.along);
            else { const away = sub(mul(add(a, b), 0.5), [W / 2, H / 2]); n = mul(n, c.side || (n[0] * away[0] + n[1] * away[1] >= 0 ? 1 : -1)); }
          }
          out = n;
          const a2 = add(a, mul(n, off)), b2 = add(b, mul(n, off));
          const e1 = [add(a, mul(n, 3)), add(a2, mul(n, 5))], e2 = [add(b, mul(n, 3)), add(b2, mul(n, 5))];
          el('polyline', { class: 'ext', points: pts(...e1) }, g); el('polyline', { class: 'ext', points: pts(...e2) }, g);
          lines.push(e1, e2);
          a = a2; b = b2;
        }
        el('polyline', { points: pts(a, b) }, g);
        lines.push([a, b]);
        const tk = [d[0] * 0.7071 - d[1] * 0.7071, d[0] * 0.7071 + d[1] * 0.7071];
        for (const p of [a, b]) el('polyline', { points: pts(add(p, mul(tk, 4.5)), add(p, mul(tk, -4.5))) }, g);
        // the text sits beyond the dimension line, square to it (or along `along`). That offset assumes the two are
        // square; `clear` is for a line `along` meets aslant (a sloping edge in an isometric, its extension lines
        // plumb), where it can put a wide label on the line: the label goes on out until its box is `clear` px from
        // the line, measured square to the line
        const tw = textW(c.text), mid = mul(add(a, b), 0.5);
        let t = 7 + Math.abs(out[0]) * tw / 2 + Math.abs(out[1]) * 7;
        if (c.clear) {
          const sq = Math.abs(out[0] * d[1] - out[1] * d[0]);   // the sine of the angle between `out` and the line
          if (sq > 0.1) t = Math.max(t, (Math.abs(d[1]) * tw / 2 + Math.abs(d[0]) * 6.5 + c.clear) / sq);
        }
        const lp = add(mid, mul(out, t));
        const x = clamp(lp[0], X0 + tw / 2, X1 - tw / 2);
        el('text', { x: x.toFixed(1), y: lp[1].toFixed(1), 'text-anchor': 'middle', 'dominant-baseline': 'middle' }, g).textContent = c.text;
        boxes.push(box(x, lp[1], tw, 13));
        if (c.sub) {
          el('text', { class: 'lbl-2', x: x.toFixed(1), y: (lp[1] + 13).toFixed(1), 'text-anchor': 'middle', 'dominant-baseline': 'middle' }, g).textContent = c.sub;
          boxes.push(box(x, lp[1] + 13, textW(c.sub), 12));
        }
      } else if (c.t === 'angle') {
        if (!ok(c.v, c.r1, c.r2)) { g.remove(); continue; }
        // The label sits just past the arc on the bisector, its box clear of both spoke edges (run out to the kit's
        // spoke-end anchors). Where it can't clear at a small radius the arc grows along the spokes (up to 80% of the
        // shorter); only then does the label move out, by at most half its width plus 10 px.
        const v = P(c.v), u1 = sub(P(c.r1), v), u2 = sub(P(c.r2), v), tw = textW(c.text);
        const bis = unit(add(unit(u1), unit(u2)));
        const end = (id, u) => (ok(id) ? P(id) : add(v, mul(u, 3)));
        const e1 = end(c.r1 + '-end', u1), e2 = end(c.r2 + '-end', u2), rMax = Math.min(len(sub(e1, v)), len(sub(e2, v))) * 0.8;
        const at = r => add(v, mul(bis, r + 6 + Math.abs(bis[0]) * tw / 2 + Math.abs(bis[1]) * 7));
        const clear = q => { const b = box(q[0], q[1], tw + 4, 15); return !crosses(v, e1, b) && !crosses(v, e2, b); };
        let r = Math.min(rMax, Math.max(16, Math.min(len(u1), len(u2)) * 0.85));
        while (r < rMax && !clear(at(r))) r = Math.min(rMax, r + 2);
        let lp = at(r);
        for (let k = 0; k < 10 + tw / 2 && !clear(lp); k += 2) lp = add(lp, mul(bis, 2));
        const p1 = add(v, mul(unit(u1), r)), p2 = add(v, mul(unit(u2), r)), cw = u1[0] * u2[1] - u1[1] * u2[0] > 0 ? 1 : 0;
        const d = `M${p1[0].toFixed(1)} ${p1[1].toFixed(1)}A${r.toFixed(1)} ${r.toFixed(1)} 0 0 ${cw} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
        el('path', { d, class: 'halo' }, g); el('path', { d }, g);
        el('text', { x: lp[0].toFixed(1), y: lp[1].toFixed(1), 'text-anchor': 'middle', 'dominant-baseline': 'middle' }, g).textContent = c.text;
        boxes.push(box(lp[0], lp[1], tw, 13));
      } else if (c.t === 'leader') {
        if (!ok(c.at)) { g.remove(); continue; }
        // `5/8" fastener (holes 11/16")` is set as two lines: the part, then the note. A plate's callout too, its
        // name, then its code as the part number, kept in its parentheses: "King post plate" over "(W4)"
        const split = c.text.match(/^(.*) \((.*)\)$/), main = split ? split[1] : c.text, note = split ? (c.plate ? `(${split[2]})` : split[2]) : '';
        const p = P(c.at), k = Math.min(1, 0.45 + s * 0.9), tw = Math.max(textW(main, 'lbl'), note ? textW(note) : 0);
        // Candidates: [dx, dy] and each alt, then their mirror images. The elbow stays on the label's side of the
        // anchor, the dot is not inside its own label, and the label and leader keep clear of what is already drawn
        // (6 px of paper from dimension lines). If none fits, it is left out.
        const own = [[c.dx, c.dy], ...(c.alt || [])], others = dots.filter(o => o.c !== c).map(o => o.b);
        let at = null;
        for (const [[dx, dy], flip] of [...own.map(q => [q, 1]), ...own.map(q => [q, -1])]) {
          const dir = (Math.sign(dx) || 1) * flip;
          const e = [clamp(p[0] + dir * Math.abs(dx) * k, dir < 0 ? X0 + tw + 12 : X0, dir > 0 ? X1 - tw - 12 : X1), clamp(p[1] + dy * k, Y0 + 8, Y1 - (note ? 20 : 8))];
          if (dir > 0 ? e[0] < p[0] + 8 : e[0] > p[0] - 8) continue;
          const sh = add(e, [dir * 8, 0]), tx = sh[0] + dir * 4;
          const lb = [dir > 0 ? tx - 3 : tx - tw - 3, sh[1] - 9, dir > 0 ? tx + tw + 3 : tx + 3, sh[1] + (note ? 21 : 9)];
          if (p[0] > lb[0] && p[0] < lb[2] && p[1] > lb[1] && p[1] < lb[3]) continue;
          if (boxes.some(b => overlaps(lb, b)) || others.some(b => overlaps(lb, b))) continue;
          if (lines.some(([q1, q2]) => crosses(q1, q2, [lb[0] - 6, lb[1] - 6, lb[2] + 6, lb[3] + 6]))) continue;
          if (boxes.some(b => crosses(p, e, b) || crosses(e, sh, b))) continue;
          at = { e, sh, tx, dir, lb }; break;
        }
        if (!at) { g.remove(); continue; }
        // `halo`: paper under the line, as under an angle's arc, for a leader whose only route crosses a dark plate
        // (the ink line alone vanishes on it)
        if (c.halo) el('polyline', { class: 'halo', points: pts(p, at.e, at.sh) }, g);
        el('polyline', { points: pts(p, at.e, at.sh) }, g);
        el('circle', { class: 'dot', cx: p[0].toFixed(1), cy: p[1].toFixed(1), r: 2.4 }, g);
        const anchor = at.dir > 0 ? 'start' : 'end';
        el('text', { class: 'lbl', x: at.tx.toFixed(1), y: at.sh[1].toFixed(1), 'text-anchor': anchor, 'dominant-baseline': 'middle' }, g).textContent = main;
        if (note) el('text', { class: 'lbl-2', x: at.tx.toFixed(1), y: (at.sh[1] + 13).toFixed(1), 'text-anchor': anchor, 'dominant-baseline': 'middle' }, g).textContent = note;
        const code = links && (c.plate || (/^([A-Z]{1,3}\d{1,2})\b/.exec(main) || [])[1]);
        const href = code && urls[code];
        if (href) {
          const a = el('a', { class: 'plk', href, 'aria-label': c.plate ? c.text : `${main}: the ${code} plate` });
          g.replaceWith(a);
          el('circle', { class: 'hit', cx: p[0].toFixed(1), cy: p[1].toFixed(1), r: Math.max(14, 24 * s).toFixed(1) }, a);
          a.append(g);
        }
        boxes.push(at.lb);
      } else { g.remove(); continue; }
      groups.push(g);
    }
    art.append(svg);
    if (animate) groups.forEach((g, i) => g.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, delay: i * 50, easing: 'ease-out', fill: 'backwards' }));
  }

  // Draws once, then again only when the drawing's size really changes (a ResizeObserver reports once as it starts
  // observing; that report must not redraw callouts that are fading in).
  function keepCallouts(fig, art, m, animate) {
    drawCallouts(fig, art, m, animate);
    let w0 = art.clientWidth, h0 = art.clientHeight;
    new ResizeObserver(() => {
      if (art.clientWidth === w0 && art.clientHeight === h0) return;
      w0 = art.clientWidth; h0 = art.clientHeight; drawCallouts(fig, art, m, false);
    }).observe(art);
  }

  /* ---------- scene drawings: idle -> preparing -> armed -> playing -> done, or straight to done ---------- */
  function sceneFigure(fig) {
    const still = fig.classList.contains('still'), url = new URL(fig.dataset.manifest, location.href).href;
    const box = fig.querySelector('.dwg-in'), full = fig.querySelector('.full');
    let state = 'idle', setupP = null, plan = null, pending = null, timer = 0, m = null, art = null, drawn = false;

    const setup = () => setupP || (setupP = manifest(url).then(mm => {
      if (!mm || fig.classList.contains('is-missing')) { setupP = null; return null; }   // a later view tries again
      m = mm; art = document.createElement('div'); art.className = 'art';
      art.style.setProperty('--ar', `${m.width} / ${m.height}`);
      box.append(art);
      return m;
    }));
    const callouts = async animate => {
      if (drawn || !(await setup()) || drawn) return;
      drawn = true; keepCallouts(fig, art, m, animate);
    };
    const finish = animate => {
      state = 'done';
      if (pending) { pending.abort(); pending = null; }   // seen before its layers were in: stop loading them
      callouts(animate);
    };

    // Near the viewport: load every layer image (after the finished drawing), then, only if the drawing is still
    // wholly out of view, put the start state over it. It is never covered while any of it can be seen.
    async function prepare() {
      if (still || state !== 'idle' || !canAnimate()) return;
      state = 'preparing';
      const shown = await within(imgLoaded(full), 8000).catch(() => false);
      const mm = await setup();
      if (state !== 'preparing') return;
      if (!shown || !mm || !mm.layers) { state = 'idle'; return; }
      const p = buildStack(mm, url);
      if (!p) { state = 'idle'; return; }
      pending = p;
      const give = () => { p.abort(); pending = null; if (state === 'preparing') state = 'idle'; };
      try { await within(p.loaded, 8000); await within(Promise.all([p.decode(0), p.decode(p.phases[0])]), 2000); }
      catch { give(); return; }
      if (state !== 'preparing' || inViewport(fig)) { give(); return; }
      pending = null; plan = p; art.prepend(p.stack); fig.classList.add('armed'); state = 'armed';
    }

    async function play() {
      if (state !== 'armed') return;
      state = 'playing'; clearTimeout(timer);
      let live = true;
      const run = playStack(art, m, plan, () => live, () => callouts(true)).catch(() => {});
      await Promise.race([run, new Promise(r => setTimeout(r, m.budget || BUDGET))]);
      live = false;
      fig.classList.remove('armed'); plan.stack.remove(); plan = null;
      finish(true);
    }

    if (full) {
      // a missing image keeps its box, as empty paper
      const miss = () => fig.classList.add('is-missing');
      full.addEventListener('error', miss);
      if (full.complete && full.naturalWidth === 0 && full.currentSrc) miss();
    }
    return {
      prepare,
      // In view. Armed: play at a quarter visible, or after 400 ms of being in view at all, so a drawing at the edge
      // of the screen never waits on more scrolling. Otherwise it is the finished drawing: draw its callouts.
      seen(e) {
        if (state === 'armed') {
          if (!e.isIntersecting) { clearTimeout(timer); timer = 0; return; }
          if (e.intersectionRatio >= 0.25) play();
          else if (!timer) timer = setTimeout(play, 400);
          return;
        }
        if (!e.isIntersecting || state === 'playing') return;
        if (state !== 'done') finish(canAnimate()); else callouts(false);
      },
    };
  }

  /* ---------- cutting-file sheets: static SVG in the HTML ---------- */
  // Finished as it is. On a sheet below the fold that was wholly out of view when this ran, the part starts on its
  // outline and is set down in its place as it comes into view, then the dimensions fade in.
  function dxfFigure(fig) {
    let armed = !fig.classList.contains('still') && canAnimate() && !inViewport(fig);
    return {
      prepare() {},
      seen(e) {
        if (!e.isIntersecting || !armed) return; armed = false;
        const sheet = [...fig.querySelectorAll('.dsheet[data-off]')].find(sv => sv.getClientRects().length);
        const part = sheet?.querySelector('.part'); if (!part) return;
        // user units are inches; a CSS translate on an SVG element is in its user units
        const [ox, oy] = sheet.dataset.off.split(' ').map(Number), from = `translate(${-ox}px, ${-oy}px)`;
        part.animate([{ transform: from }, { transform: from, offset: 0.25 }, { transform: 'none' }], { duration: 1000, delay: 120, easing: EASE, fill: 'backwards' });
        sheet.querySelectorAll('.dxf-dims > *, .dl').forEach((g, i) => g.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 240, delay: 820 + i * 60, easing: 'ease-out', fill: 'backwards' }));
      },
    };
  }

  /* ---------- wiring ---------- */
  const ctl = new Map(figs.map(f => [f, f.dataset.dxf ? dxfFigure(f) : sceneFigure(f)]));
  if (!('IntersectionObserver' in window)) {
    figs.forEach(f => ctl.get(f).seen({ isIntersecting: true, intersectionRatio: 1 }));
    return;
  }
  const vis = new IntersectionObserver(es => es.forEach(e => ctl.get(e.target).seen(e)), { threshold: [0, 0.25] });
  figs.forEach(f => vis.observe(f));

  /* ---------- point buttons that swap a tile's drawing ---------- */
  // The Vaulted ceiling tile's point buttons (sections/home-tiles.liquid: .pts[data-swaps], each <a> naming its
  // system's scene). Hovered with a mouse, or focused from the keyboard, a button shows its system's drawing in the
  // tile, with that drawing's callouts; leaving shows the tile's own (the 4 points) again. The kit frames the four
  // alike, so they share one box and nothing moves. The others load once, after the page has loaded and the tile is in
  // view, and only on a device that hovers (a keyboard focus loads them anywhere); until one has loaded and decoded,
  // its button leaves the tile as it is. A touch never swaps, so a tap still just opens the designer. The swap is a
  // short fade in CSS, none with reduced motion. The tie rod hub's steel collar ties card does the same with its
  // options (sections/page-tierods-cases-systems.liquid: [data-point-swaps], each link naming its scene and its points
  // count in data-n, since its text is a whole label).
  const hovers = matchMedia('(hover: hover) and (pointer: fine)');
  document.querySelectorAll('.pts[data-swaps], [data-point-swaps]').forEach(group => {
    const fig = group.closest('.tile-a, .trc-sys-a')?.querySelector('.dwg[data-manifest]');
    const box = fig?.querySelector('.dwg-in'), full = box?.querySelector('.full');
    if (!full) return;
    const base = fig.dataset.scene, buttons = [...group.querySelectorAll('a[data-scene]')], alts = new Map();
    let ready = null, want = base, shown = base, timer = 0;
    fig.classList.add('swap');
    // the tile's note ("Drawn: a 4-point ceiling, in elevation") names the system shown
    const note = group.parentElement.querySelector('.tile-note'), noteText = note?.textContent || '';
    const pointsOf = b => b?.dataset.n || b?.textContent.trim();
    const baseN = pointsOf(buttons.find(b => b.dataset.scene === base));
    const pointsRe = baseN && new RegExp(`\\b${baseN}-point\\b`);

    const apply = scene => {
      if (scene === shown) return;
      for (const [k, L] of alts) { const on = k === scene; L.img.classList.toggle('on', on); L.art?.classList.toggle('on', on); }
      fig.classList.toggle('swapped', scene !== base);
      if (note) {
        // a link's own note when it has one (data-note), else the tile's note with the points count changed
        const b = buttons.find(x => x.dataset.scene === scene);
        if (scene === base) note.textContent = noteText;
        else if (b?.dataset.note) note.textContent = b.dataset.note;
        else if (pointsRe) note.textContent = noteText.replace(pointsRe, `${pointsOf(b) || baseN}-point`);
      }
      shown = scene;
    };
    const show = scene => {
      want = scene;
      if (scene !== base && !alts.get(scene)?.ok) { load(); scene = base; }   // shown once it has loaded, if still wanted
      apply(scene);
    };
    const load = () => ready || (ready = Promise.all(buttons.filter(a => a.dataset.scene !== base && a.dataset.srcset).map(async a => {
      const scene = a.dataset.scene, img = new Image();
      img.className = 'full alt'; img.alt = ''; img.decoding = 'async'; img.fetchPriority = 'low';
      img.width = full.width; img.height = full.height; img.sizes = full.sizes; img.srcset = a.dataset.srcset;
      const L = { img, art: null, ok: false };
      alts.set(scene, L); box.append(img);
      const [shownOk, m] = await Promise.all([imgLoaded(img), manifest(new URL(a.dataset.manifest, location.href).href)]);
      if (!shownOk) { img.remove(); alts.delete(scene); return; }
      await img.decode().catch(() => {});
      if (m) {
        const art = document.createElement('div'); art.className = 'art alt';
        art.style.setProperty('--ar', `${m.width} / ${m.height}`);
        box.append(art); keepCallouts(fig, art, m, false); L.art = art;
      }
      L.ok = true;
      if (want === scene) apply(scene);
    })).catch(() => {}));

    // Leaving a button waits a moment, so moving across the gap to the next one doesn't flash the 4 points between;
    // a button that still has the keyboard's focus keeps its drawing.
    const enter = a => { clearTimeout(timer); show(a.dataset.scene); };
    const leave = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const f = buttons.find(b => b === document.activeElement && b.matches(':focus-visible'));
        show(f ? f.dataset.scene : base);
      }, 90);
    };
    for (const a of buttons) {
      a.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') enter(a); });
      a.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse') leave(); });
      a.addEventListener('focus', () => { if (a.matches(':focus-visible')) enter(a); });
      a.addEventListener('blur', leave);
    }

    let pageLoaded = document.readyState === 'complete', inView = false;
    const maybe = () => { if (pageLoaded && inView && hovers.matches) { near.disconnect(); load(); } };
    const near = new IntersectionObserver(es => { inView = es.some(e => e.isIntersecting); maybe(); });
    near.observe(fig);
    if (!pageLoaded) addEventListener('load', () => { pageLoaded = true; maybe(); }, { once: true });
  });

  /* Assembly: only figures marked data-assemble, and only when it is cheap. Nothing loads for it until the page has
     loaded and the visitor has left the top of the page (an observer on the header, not a scroll handler; a jump to a
     section counts), so a visitor who stays on the first screen downloads no layers. Then a drawing is prepared when
     it is near (900 px ahead, 400 on a phone), one at a time. Data Saver or a 2G/3G connection: no assembly. */
  if (!canAnimate()) return;
  const conn = navigator.connection || {};
  if (conn.saveData || /^(slow-2g|2g|3g)$/.test(conn.effectiveType || '')) return;
  const heroes = figs.filter(f => f.hasAttribute('data-assemble') && !f.classList.contains('still') && !f.dataset.dxf);
  if (!heroes.length) return;
  let queue = Promise.resolve(), loaded = document.readyState === 'complete', moved = false, started = false;
  const near = new IntersectionObserver(es => es.forEach(e => {
    if (!e.isIntersecting) return; near.unobserve(e.target);
    const c = ctl.get(e.target); queue = queue.then(() => c.prepare()).catch(() => {});
  }), { rootMargin: `0px 0px ${innerWidth < 700 ? 400 : 900}px 0px` });
  const begin = () => { if (started || !loaded || !moved) return; started = true; heroes.forEach(f => near.observe(f)); };
  const hdr = document.querySelector('.hdr');
  if (hdr) {
    const away = new IntersectionObserver(es => {
      if (es.some(e => e.intersectionRatio < 0.99)) { moved = true; away.disconnect(); begin(); }
    }, { threshold: [0.99, 1] });
    away.observe(hdr);
  } else moved = true;
  if (!loaded) addEventListener('load', () => { loaded = true; begin(); }, { once: true });
})();

// Videos marked data-autoplay play by themselves (muted, inline) while they are in view and pause when they leave,
// so nothing plays off screen. Never on reduced motion, Save-Data or a 2G/3G connection: those keep the poster and
// the play button. A visitor who pauses one keeps it paused. preload="none" on the element means nothing downloads
// until the video first comes into view. Such a video's browser controls are swapped for its frame's one play/pause
// button (.vid-tg) and a click on the film, so it can always be stopped, or started where it doesn't play itself.
(() => {
  const vids = document.querySelectorAll('video[data-autoplay]');
  vids.forEach(v => {
    const b = v.parentElement.querySelector('.vid-tg');
    if (!b) return;
    const sync = () => {
      b.dataset.state = v.paused ? 'paused' : 'playing';
      b.setAttribute('aria-label', v.paused ? b.dataset.play : b.dataset.pause);
    };
    const toggle = () => { if (v.paused) v.play()?.catch(() => {}); else v.pause(); };
    v.controls = false;
    v.addEventListener('play', sync);
    v.addEventListener('pause', sync);
    v.addEventListener('click', toggle);
    b.addEventListener('click', toggle);
    sync();
    b.hidden = false;
  });
  if (!vids.length || !('IntersectionObserver' in window)) return;
  const net = navigator.connection;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches || (net && (net.saveData || /2g|3g/.test(net.effectiveType || '')))) return;
  const ours = new WeakSet();
  const seen = new IntersectionObserver(es => es.forEach(e => {
    const v = e.target;
    if (v.dataset.userPaused) return;
    if (e.isIntersecting) { v.muted = true; v.play()?.catch(() => {}); }
    else if (!v.paused) { ours.add(v); v.pause(); }
  }), { threshold: 0.35 });
  vids.forEach(v => {
    v.addEventListener('pause', () => { if (ours.has(v)) ours.delete(v); else if (!v.ended) v.dataset.userPaused = '1'; });
    v.addEventListener('play', () => { delete v.dataset.userPaused; });
    seen.observe(v);
  });
})();

// Before and after (sections/page-compare): the divide follows a drag anywhere on the picture (or a tap), and the
// arrow keys on its slider. Nothing moves on its own. Vertical swipes still scroll the page (CSS touch-action: pan-y).
document.querySelectorAll('[data-cmp]').forEach(f => {
  const box = f.querySelector('.cmp-box'), range = f.querySelector('.cmp-range');
  if (!box || !range) return;
  const set = v => {
    v = Math.max(0, Math.min(100, v));
    f.style.setProperty('--pos', v + '%');
    range.value = String(Math.round(v));
  };
  const at = e => { const r = box.getBoundingClientRect(); set(((e.clientX - r.left) / r.width) * 100); };
  let drag = null;
  box.addEventListener('pointerdown', e => {
    if (e.button !== 0) return;
    drag = e.pointerId;
    box.setPointerCapture(drag);
    at(e);
  });
  box.addEventListener('pointermove', e => { if (e.pointerId === drag) at(e); });
  const end = e => { if (e.pointerId === drag) drag = null; };
  box.addEventListener('pointerup', end);
  box.addEventListener('pointercancel', end);
  range.addEventListener('input', () => set(+range.value));
  range.hidden = false;
  f.dataset.ready = '';
});
